import { supabaseClient } from "@/lib/supabase";
import type { PatientsSearchT } from "@/routes/_app/patients";
import type {
	DiariesCountT,
	DiaryViewT,
	StatefulDiaryT,
} from "@/types/database/diaries";
import type { UuidT } from "@/types/zod/auth";
import type { Auth } from "@/utils/auth";
import { validate as validateUUID } from "uuid";
import { countBy } from "./utils";

// Core API functions
export async function getStatefulDiaries(
	filters: Record<string, unknown> = {},
): Promise<StatefulDiaryT[]> {
	const { data, error } = await supabaseClient.functions.invoke(
		"public-get-diaries",
		{ body: filters },
	);

	if (error) {
		console.error("Error fetching diaries:", error);
		throw error;
	}

	return data || [];
}

export async function getDiaryById(
	diaryId: string,
): Promise<StatefulDiaryT | null> {
	if (!validateUUID(diaryId)) {
		console.warn(`Invalid UUID provided to getDiaryById: ${diaryId}`);
		return null;
	}

	const { data, error } = await supabaseClient.functions.invoke(
		"public-get-diaries",
		{
			body: { diary_id: diaryId },
		},
	);

	if (error) {
		console.error(`Error fetching diary ${diaryId}:`, error);
		throw error;
	}

	if (!data) {
		console.error("No data returned from getDiaries");
		throw new Error("No data returned from getDiaries");
	}

	return data?.[0] || null;
}

export async function getDiariesByPatient(
	patientId: UuidT,
): Promise<StatefulDiaryT[]> {
	if (!validateUUID(patientId)) {
		console.warn(`Invalid UUID provided to getDiariesByPatient: ${patientId}`);
		return [];
	}

	return getStatefulDiaries({ patient_id: patientId });
}

export async function getDiariesTabs(
	filters: Record<string, unknown> = {},
): Promise<{ tab: string | null }[]> {
	let query = supabaseClient.from("diaries_view").select("tab");

	if (filters.user_id) {
		query = query.contains("direct_access", [filters.user_id]);
	}

	if (
		filters.diary_access &&
		Array.isArray(filters.diary_access) &&
		filters.diary_access.length > 0
	) {
		query = query.overlaps("direct_access", filters.diary_access);
	}

	if (
		filters.organisations &&
		Array.isArray(filters.organisations) &&
		filters.organisations.length > 0
	) {
		query = query.in("organisation_id", filters.organisations);
	}

	const { data, error } = await query.returns<{ tab: string | null }[]>();

	if (error) {
		console.error("Error fetching diary tabs:", error);
		throw error;
	}

	return data || [];
}

export const prepDiariesData = (settings: PatientsSearchT, auth?: Auth) => {
	if (!settings?.tab) throw new Error("Tab is required");
	if (!auth) throw new Error("Not logged in");

	return {
		...settings,
		diary_access: settings?.diary_access ?? undefined,
		user_id: settings?.diaries === "mine" ? auth.id : undefined,
	};
};

export async function getAllDiaries(): Promise<DiaryViewT[]> {
	const { data, error } = await supabaseClient.from("diaries_view").select("*");

	if (error) throw error;

	return data as DiaryViewT[];
}

export async function getDiariesBySettings(
	settings: PatientsSearchT,
	auth: Auth,
): Promise<DiaryViewT[]> {
	let query = supabaseClient
		.from("diaries_view")
		.select(
			"diary_id, diary_name,state,diary_status,is_read, organisation_name, patient_info, hospital_number, tab, start_date, end_date",
		);

	const { tab, user_id, diary_access, organisations } = prepDiariesData(
		settings,
		auth,
	);

	if (tab) {
		query = query.eq(
			"tab",
			tab.toLowerCase() as "active" | "pending" | "inactive",
		);
	}

	if (user_id) {
		query = query.contains("direct_access", [user_id]);
	}

	if (diary_access && diary_access?.length > 0) {
		query = query.overlaps("direct_access", diary_access);
	}

	if (organisations && organisations?.length > 0) {
		query = query.in("organisation_id", organisations);
	}

	const { data: diaries, error } = await query;

	if (error) throw error;

	return diaries as DiaryViewT[];
}

export async function getCountBySettings(
	settings: PatientsSearchT,
	auth: Auth,
): Promise<DiariesCountT> {
	const { user_id, diary_access, organisations } = prepDiariesData(
		settings,
		auth,
	);

	const filters = {
		user_id,
		diary_access,
		organisations,
	};

	const diaries = await getDiariesTabs(filters);

	if (!diaries || diaries.length === 0) {
		return {
			active: 0,
			pending: 0,
			inactive: 0,
		};
	}

	const tabCounts = countBy(diaries, (diary) => diary.tab ?? "unknown");

	return {
		active: tabCounts.active ?? 0,
		pending: tabCounts.pending ?? 0,
		inactive: tabCounts.inactive ?? 0,
	};
}
